import { IconButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Split, Stack } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { EmailLine } from "@dropbox/dig-icons/assets";
import { ButtonLink } from "@mirage/mosaics/BaseProfilePage/Link";
import { UserProfileCustomizedBackgroundImage } from "@mirage/mosaics/UserProfilePage/background/CustomizedBackground";
import { useUserProfileApi } from "@mirage/mosaics/UserProfilePage/hooks";
import { UserProfileAvatar } from "@mirage/mosaics/UserProfilePage/UserProfileAvatar";
import { UserProfileRole } from "@mirage/mosaics/UserProfilePage/UserProfileInfo";
import {
  getUserProfilePageHref,
  isValidEmail,
} from "@mirage/mosaics/UserProfilePage/utils";
import { openURL } from "@mirage/service-platform-actions";
import i18n from "@mirage/translations";

import type { PersonOrgChart } from "@mirage/service-people/types";

const DEFAULT_CARD_WIDTH = 398;

export const BasicUserProfileCard = ({
  person,
  onViewProfile,
}: {
  person: PersonOrgChart;
  onViewProfile: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) => {
  const userInfo = useUserProfileApi({ displayHandle: person.displayHandle });
  if (!userInfo) {
    return null;
  }
  return (
    <Box
      as={Stack}
      backgroundColor="Background Raised"
      borderColor="Border Subtle"
      borderRadius="Large"
      borderStyle="Solid"
      borderWidth="1"
      boxShadow="Floating"
      cursor="default"
      overflow="hidden"
      style={{ width: DEFAULT_CARD_WIDTH }}
    >
      <UserProfileCustomizedBackgroundImage
        user={userInfo}
        borderRadius="None"
        height="112px"
      />
      <Box padding="24" paddingTop="0">
        <Box
          style={{
            marginTop: "-89px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <UserProfileAvatar
            user={{
              name: person.displayName,
              profile_photo_url: person.profilePhotoUrl,
              is_deactivated: person.isDeactivated,
            }}
            size="profile"
          />
        </Box>
        <Box marginTop="12">
          <Text size="xlarge" isBold>
            {person.displayName}
          </Text>
        </Box>
        <UserProfileRole
          user={{
            role: person.role ?? null,
            team_names: person.teams,
          }}
          textSize="medium"
        />
        <Split gap="8" style={{ marginTop: "6px" }}>
          <Split.Item>
            <ButtonLink
              variant="outline"
              to={getUserProfilePageHref(person.displayHandle)}
              onClick={onViewProfile}
              style={{ borderColor: "var(--dig-color__border__subtle)" }}
            >
              {i18n.t("view_profile")}
            </ButtonLink>
          </Split.Item>
          {isValidEmail(person.email) && (
            <Split.Item>
              <IconButton
                variant="opacity"
                style={{ borderRadius: "var(--spacing__unit--1)" }}
                onClick={() => {
                  openURL(`mailto:${person.email}`);
                }}
              >
                <UIIcon src={EmailLine} />
              </IconButton>
            </Split.Item>
          )}
        </Split>
      </Box>
    </Box>
  );
};
